import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * The grid pager, the mockup's `.pager`: "View 24 more" appends the next page of cards,
 * "Show fewer" takes the last page away again, and the count, the bar and `?page=` follow.
 *
 * State lives in data attributes rendered by snippets/hajjo-pager.liquid, so a section
 * re-render (a filter or a sort) resets it from the server and nothing here goes stale.
 * Both controls are real links: if this module has not loaded, or a fetch fails, the browser
 * simply goes to that page.
 *
 * @typedef {object} HajjoPagerRefs
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [bar]
 * @property {HTMLAnchorElement} [more]
 * @property {HTMLAnchorElement} [fewer]
 * @property {HTMLElement} [label]
 *
 * @extends {Component<HajjoPagerRefs>}
 */
class HajjoPager extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#fillEarlierPages();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#abortController?.abort();
    this.#fillEarlierPages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * Appends the next page of cards.
   * @param {MouseEvent} event
   */
  async showMore(event) {
    const { more } = this.refs;
    if (!more || more.getAttribute('aria-busy') === 'true') return;

    const next = this.#lastPage + 1;
    if (next > this.#pages) return;

    event.preventDefault();
    this.#setBusy(true);

    try {
      const cards = await this.#fetchCards(next);
      const grid = this.#grid;
      if (!grid) throw new Error('No product grid');

      grid.append(...cards);
      this.dataset.lastPage = String(next);
      this.#paint();
      this.#writePage();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // The fetch failed: let the link do what it says.
      window.location.href = more.href;
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Takes the last page of cards away again.
   * @param {MouseEvent} event
   */
  showFewer(event) {
    const last = this.#lastPage;
    const first = this.#firstPage;
    const grid = this.#grid;

    // Nothing appended here to take back: the link goes to the previous page.
    if (!grid || last <= first) return;

    event.preventDefault();

    for (const card of grid.querySelectorAll(`:scope > [data-page="${last}"]`)) card.remove();

    this.dataset.lastPage = String(last - 1);
    this.#paint();
    this.#writePage();

    const top = grid.getBoundingClientRect().top + window.scrollY - 140;
    if (window.scrollY > top) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  /**
   * A deep link (?page=3) arrives with only page 3 rendered. Fill in pages 1 and 2 above it
   * so the grid reads from the start, as the mockup's does, keeping the scroll position.
   */
  async #fillEarlierPages() {
    let first = this.#firstPage;
    if (first <= 1) {
      this.#paint();
      return;
    }

    for (let page = first - 1; page >= 1; page--) {
      try {
        const cards = await this.#fetchCards(page);
        const grid = this.#grid;
        if (!grid || !this.isConnected) return;

        const anchor = grid.firstElementChild;
        const before = anchor ? anchor.getBoundingClientRect().top : 0;
        grid.prepend(...cards);
        if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before);

        first = page;
        this.dataset.firstPage = String(page);
      } catch {
        break;
      }
    }

    this.#paint();
  }

  /**
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #fetchCards(page) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';

    const html = await sectionRenderer.getSectionHTML(this.#sectionId, true, url, signal);
    if (signal.aborted) throw new DOMException('Superseded', 'AbortError');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector('[ref="grid"]');
    if (!grid) throw new Error('No grid in the page response');

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  /** Count, bar and the two controls, from the data attributes. */
  #paint() {
    const { count, bar, more, fewer, label } = this.refs;
    const total = this.#total;
    const size = this.#pageSize;
    const first = this.#firstPage;
    const last = this.#lastPage;
    const shown = Math.min(total, last * size) - (first - 1) * size;
    const left = Math.max(0, total - last * size);
    const done = last >= this.#pages && first <= 1;

    if (count) {
      count.innerHTML = (this.dataset.countTemplate || '')
        .replace('__SHOWN__', String(shown))
        .replace('__TOTAL__', String(total));
    }
    if (bar) bar.style.width = `${total ? Math.round((Math.min(total, last * size) / total) * 100) : 0}%`;

    if (more) {
      more.hidden = last >= this.#pages;
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('page', String(last + 1));
      more.href = nextUrl.toString();
    }
    if (label) label.textContent = (this.dataset.moreTemplate || '').replace('__N__', String(Math.min(size, left)));

    if (fewer) {
      // Show fewer only takes back pages appended here; on a deep link that could not be
      // filled it stays a link to the previous page.
      fewer.hidden = last <= 1;
      if (last > 1) {
        const previousUrl = new URL(window.location.href);
        previousUrl.searchParams.set('page', String(last - 1));
        fewer.href = previousUrl.toString();
      }
    }

    this.toggleAttribute('data-done', done);
  }

  /** Keep the page in the address so a shared link or a reload lands on the same grid. */
  #writePage() {
    const url = new URL(window.location.href);
    if (this.#lastPage > 1) url.searchParams.set('page', String(this.#lastPage));
    else url.searchParams.delete('page');
    try {
      history.replaceState(history.state, '', url);
    } catch {
      // Some embedded previews refuse replaceState; the grid still works.
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { more, label } = this.refs;
    if (!more) return;

    if (busy) {
      more.setAttribute('aria-busy', 'true');
      more.setAttribute('aria-disabled', 'true');
      if (label) {
        label.dataset.restLabel = label.textContent || '';
        label.textContent = this.dataset.busyLabel || label.textContent;
      }
    } else {
      more.removeAttribute('aria-busy');
      more.removeAttribute('aria-disabled');
      if (label && label.dataset.restLabel && label.textContent === this.dataset.busyLabel) {
        label.textContent = label.dataset.restLabel;
      }
      this.#paint();
    }
  }

  /** @returns {HTMLElement | null} */
  get #grid() {
    return this.parentElement?.querySelector(':scope > [ref="grid"]') ?? null;
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #total() {
    return Number(this.dataset.total) || 0;
  }

  get #pageSize() {
    return Number(this.dataset.pageSize) || 24;
  }

  get #pages() {
    return Number(this.dataset.pages) || 1;
  }

  get #firstPage() {
    return Number(this.dataset.firstPage) || 1;
  }

  get #lastPage() {
    return Number(this.dataset.lastPage) || this.#firstPage;
  }
}

if (!customElements.get('hajjo-pager')) {
  customElements.define('hajjo-pager', HajjoPager);
}
